// Phase-0 trigger harness — prove the filter→annotate model on REAL
// prompts, entirely client-side, by reusing the kernel runner.
//
// A "trigger" is a render-less kernel (the client-only kernels in
// exampleTriggers.js, the extractors in builtinExtractors.js): its
// steps run over ONE prompt event (bound as the `event` param) and its
// terminal step returns an annotation object — or null, meaning the
// trigger didn't fire. There is NO new evaluator here: a trigger is a
// kernel whose input is one event instead of a population scope and
// whose terminal is an annotation instead of a render.
//
// The loop mirrors the dashboard's own list→detail pattern:
//   1. sampleTriggerEvents — one analytics query for a batch of prompt
//      event_ids + cheap scalar dims (this is the surface a production
//      streaming pre-filter would gate on), then
//   2. hydratePrompt reads normalized messages attributed to each selected capture.
//
// Nothing here is mocked: real data in, real annotations out. Signals the
// backend is actively producing are READ from durable `signal_tags`
// (runSignalsOverTraceIds routes them off the analytics graph); the rest are
// still computed client-side over a SAMPLED window — a preview (we sample
// instead of tailing a stream, and any cross-event counter is over the sample
// only) — and migrate to the backend automatically as they're ported and enabled.

import { runQueryRows, rangeToTimeRange, getSignalTags, getActiveSignalNamespaces } from "../analyticsClient.js";
import { fieldsWithoutValue, missingValueMessage, runKernel, substituteParams } from "../kernelRunner.js";
import { formatConversationValue } from "../conversationContent.js";
import { readObservedCapture } from "../conversationRead.js";
import { lastSignalTurnTextWhere, turnText, toContentString } from "./promptSurfaces.js";
import { planQueries, sliceResults } from "../signals/queryPlanner.js";
import { listTriggers } from "./triggerStore.js";
import {
    CLIENT_ONLY_TRIGGERS,
    REFERENCE_TRIGGERS,
} from "./exampleTriggers.js";
import { BUILTIN_EXTRACTORS } from "./builtinExtractors.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../authTokenStore.js";
import {
    RUN_SURFACES,
    asResolvedKernel,
    createBundledRunContext,
    createKernelResolver,
    createUnknownRunContext,
} from "../kernelResolver.js";

// Identity, not a body flag/id supplied at runtime, proves which trigger
// definitions shipped in this dashboard artifact.
const SHIPPED_TRIGGER_BODIES = Object.freeze([
    ...listTriggers(),
    ...REFERENCE_TRIGGERS,
    ...CLIENT_ONLY_TRIGGERS,
    ...BUILTIN_EXTRACTORS,
]);
const SHIPPED_TRIGGER_SET = new Set(SHIPPED_TRIGGER_BODIES);
const SIGNAL_EVENT_TEXT_MAX_SCALARS = 1_000_000;
const SHIPPED_TRIGGER_RESOLVER = createKernelResolver({
    bundledKernels: SHIPPED_TRIGGER_BODIES,
});

function triggerRunPolicy(trigger) {
    const shipped = SHIPPED_TRIGGER_SET.has(trigger);
    const resolvedDefinition = shipped
        ? SHIPPED_TRIGGER_RESOLVER.resolveById(trigger?.id)
            || SHIPPED_TRIGGER_RESOLVER.resolveByCommand(trigger?.command)
        : asResolvedKernel(trigger);
    return {
        resolvedDefinition,
        runContext: shipped
            ? createBundledRunContext({ surface: RUN_SURFACES.BACKGROUND })
            : createUnknownRunContext({ surface: RUN_SURFACES.BACKGROUND }),
    };
}

// ── 1. Sample: a batch of candidate prompt event_ids ───────────────
//
// EVENT-rooted (events.events is the source of truth for "an AiPrompt
// happened"), scalar dims only so there's no arrayJoin fan-out — one
// row per event. provider/model live on the PROMPT left-join and are
// pulled in hydratePrompt instead, keeping this query minimal.
export async function sampleTriggerEvents({ range = null, limit = 50 } = {}) {
    const spec = {
        root: { alias: "e", entity: "ENTITY_TYPE_EVENT" },
        timeRange: rangeToTimeRange(range),
        filter: {
            pred: {
                field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TYPE" },
                op: "OPERATOR_EQ", value: "AiPrompt",
            },
        },
        output: {
            dimensions: [
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },        outputAlias: "event_id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_NOTIFICATION_ID" }, outputAlias: "session_id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
            ],
        },
        orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_DESC" }],
        limit,
    };
    const rows = await runQueryRows(spec);
    // EVENT isn't FINAL'd by the planner (~12% dupe-by-event_id from
    // re-ingestion; see apiClient getTraces). Dedup, newest kept.
    const seen = new Set();
    const out = [];
    for (const r of rows) {
        const id = r.event_id != null ? String(r.event_id) : null;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({
            event_id: id,
            endpoint_id: r.endpoint_id != null ? String(r.endpoint_id) : null,
            session_id: r.session_id != null ? String(r.session_id) : null,
            event_time: r.event_time,
        });
    }
    return out;
}

// Hydrate normalized messages attributed to one captured observation.
// A caller may reuse already loaded conversation data. Otherwise the result
// marks the unavailable exact model-call context, including tool definitions.
export async function hydratePrompt(eventId, { metadata = null, requestContext = null, signal = null, parsed: preloaded = null } = {}) {
    const wideRange = { relative: { durationSeconds: String(90 * 86400) } };
    const spec = {
        root: {
            alias: "e", entity: "ENTITY_TYPE_EVENT",
            children: [{
                edge: "EDGE_TYPE_EVENT_HAS_PROMPT", mode: "JOIN_MODE_LEFT",
                target: { alias: "p", entity: "ENTITY_TYPE_PROMPT", stepFilter: [] },
            }],
        },
        timeRange: wideRange,
        filter: {
            pred: {
                field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },
                op: "OPERATOR_EQ", value: eventId,
            },
        },
        output: {
            dimensions: [
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },        outputAlias: "event_id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_NOTIFICATION_ID" }, outputAlias: "session_id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
                { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },       outputAlias: "provider" },
                { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MODEL" },          outputAlias: "model" },
            ],
        },
        limit: 1,
    };
    const rows = metadata ? [metadata] : await runQueryRows(spec, { signal, ...(requestContext === null ? {} : { cache: false, requestContext }) });
    const focal = rows[0];
    if (!focal) return null;
    if (String(focal.event_id) !== String(eventId)) throw new Error("Conversation metadata does not match the selected event.");

    const parsed = preloaded && Array.isArray(preloaded.turns)
        ? { turns: preloaded.turns, toolDefs: Array.isArray(preloaded.toolDefs) ? preloaded.toolDefs : [], partialBody: Boolean(preloaded.partialBody), partialBodyReason: preloaded.partialBodyReason, requestContextRetired: Boolean(preloaded.requestContextRetired) }
        : await readObservedCapture({
            traceId: focal.session_id, sourceEventId: eventId, timeRange: wideRange, signal, requestContext,
        });
    // Signals receive the existing structured turn contract. Full item detail
    // also stays available to corpus readers without rebuilding provider JSON.
    const toolCalls = lastToolCalls(parsed.turns);
    return {
        event_id: String(focal.event_id),
        endpoint_id: focal.endpoint_id != null ? String(focal.endpoint_id) : null,
        session_id: focal.session_id != null ? String(focal.session_id) : null,
        event_time: focal.event_time,
        provider: focal.provider || "",
        model: focal.model || "",
        text: Array.from(lastSignalTurnTextWhere(
            parsed.turns,
            (turn) => turn?.role === "user",
        ).text)
            .slice(0, SIGNAL_EVENT_TEXT_MAX_SCALARS)
            .join(""),
        turns: normalizedTurns(parsed.turns),
        tool_calls: toolCalls,
        tools_used: deriveToolsUsed(toolCalls),
        tool_results: lastToolResults(parsed.turns),
        tool_defs: Array.isArray(parsed.toolDefs) ? parsed.toolDefs.map(simpleToolDef) : [],
        resource_paths: deriveResourcePaths(toolCalls),
        parsedConversation: parsed,
        partialBody: parsed.partialBody,
        partialBodyReason: parsed.partialBodyReason,
        requestContextRetired: Boolean(parsed.requestContextRetired),
    };
}

// ── 3. Run one trigger over one event ──────────────────────────────
//
// The trigger IS a kernel; we bind the event as the `event` param and
// read the terminal step's output (renderData) as the annotation. null
// terminal output = the trigger's gate didn't fire.
export async function runTrigger(trigger, event, runOpts = null) {
    if (usesCtxQuery(trigger)) {
        return {
            fired: false,
            annotation: null,
            error: new Error(
                "ctx.query is unsupported in isolated Python; declare an analytics_query step and read its JSON rows through inputs",
            ),
            stepSummaries: [],
        };
    }
    const { resolvedDefinition, runContext } = triggerRunPolicy(trigger);
    const res = await runKernel({
        ...(runOpts?.precomputed ? { precomputed: runOpts.precomputed } : {}),
        ...(runOpts?.signal ? { signal: runOpts.signal } : {}),
        resolvedDefinition,
        runContext,
        params: { event },
    });
    return interpretKernelResult(res);
}

// kernel result → trigger verdict (shared by every evaluation path).
function interpretKernelResult(res) {
    if (!res.ok) {
        return { fired: false, annotation: null, error: res.error, stepSummaries: res.stepSummaries || [] };
    }
    const raw = res.renderData ?? null;
    // A converged signal returns a LIST of backend-shaped tags
    // (`run(event) -> list[tag]`); a legacy kernel trigger (a user-authored or
    // contextual-lookup signal, e.g. the REFERENCE_TRIGGERS) returns a single
    // annotation object, or null. Adapt a tag list to the dashboard's annotation shape so
    // the store / lenses / badge — which read `{type, value, confidence}` —
    // stay unchanged. An empty list means the signal didn't fire.
    const annotation = Array.isArray(raw) ? tagsToAnnotation(raw) : raw;
    return { fired: annotation != null, annotation, error: null, stepSummaries: res.stepSummaries || [] };
}

// Backend tag list (`{subject_type, namespace, tag, value}`) → the dashboard's
// display annotation. The tag list is the normalized, DB-friendly form; the
// annotation object is the denormalized view the badge / `signalDetail` consume
// (see docs/specs/signals-contract.md "tag — the output"). One badge per
// signal: `type` = `<namespace>.<tag>` of the first tag (its first segment
// drives the badge accent), and the findings collapse to `{match}` (single) or
// `{hits:[{kind, match}]}` (multi), both of which `signalDetail` already renders.
// `confidence` rides along if a tag carries it (backend strips it; dashboard-only).
export function tagsToAnnotation(tags) {
    if (!Array.isArray(tags) || tags.length === 0) return null;
    const first = tags[0] || {};
    const ns = (typeof first.namespace === "string" && first.namespace) || "signal";
    const tg = (typeof first.tag === "string" && first.tag) || "fired";
    const conf = tags.map((t) => t && t.confidence).find((c) => typeof c === "number");
    const value = tags.length === 1
        ? { match: tags[0].value != null ? String(tags[0].value) : null }
        : { hits: tags.map((t) => ({ kind: t && t.tag, match: t && t.value != null ? String(t.value) : null })) };
    // Carry each finding's highlight region ({surface, index, start, end}, plus an
    // optional `detail` — the finding's own display text, for marks whose matched
    // span is invisible) so the trace-detail view can mark exactly where the signal
    // fired. Offsets locate a redacted secret without the verdict carrying it.
    // See docs/specs/signals-contract.md.
    const locs = tags.map((t) => t && t.loc).filter(Boolean);
    if (locs.length) value.locs = locs;
    return {
        subject_type: first.subject_type || "session",
        type: `${ns}.${tg}`,
        value,
        ...(typeof conf === "number" ? { confidence: conf } : {}),
    };
}

// ── 4. Orchestrate: sample → hydrate (concurrency-capped) → run ────
//
// One-at-a-time body hydration is the documented constraint; we just
// run `concurrency` of them in flight. onProgress fires after each
// event so the panel can stream results in.
export async function runTriggerOverSample(trigger, { range = null, limit = 50, concurrency = 6, onProgress } = {}) {
    const candidates = await sampleTriggerEvents({ range, limit });
    const total = candidates.length;
    const results = new Array(total);
    let done = 0;
    let next = 0;

    async function worker() {
        while (next < candidates.length) {
            const i = next++;
            const cand = candidates[i];
            let rec;
            try {
                const event = await hydratePrompt(cand.event_id);
                if (!event) {
                    rec = { event_id: cand.event_id, fired: false, annotation: null, error: new Error("hydrate returned no row"), event: null, stepSummaries: [] };
                } else {
                    const r = await runTrigger(trigger, event);
                    rec = { event_id: cand.event_id, fired: r.fired, annotation: r.annotation, error: r.error, stepSummaries: r.stepSummaries, event };
                }
            } catch (err) {
                rec = { event_id: cand.event_id, fired: false, annotation: null, error: err instanceof Error ? err : new Error(String(err)), event: null, stepSummaries: [] };
            }
            results[i] = rec;
            done++;
            try { onProgress?.({ done, total, last: rec }); } catch { /* noop */ }
        }
    }

    const workerCount = Math.min(Math.max(1, concurrency), Math.max(1, total));
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    const annotations = results.filter((r) => r && r.fired && r.annotation).map((r) => r.annotation);
    return { total, results: results.filter(Boolean), annotations };
}

// ── 5. Run signals over an existing set of trace ids (live badging) ─
//
// For the Explore trace listing: we already have the rows (their event
// ids), so we hydrate each ONCE and run every selected trigger over it,
// reporting per-id as soon as a row finishes (so badges pop in). Only
// "cheap" triggers — all-python, no per-row I/O — belong here; the
// caller filters to those so this stays one hydrate per row.
//
//   onResult(traceId, fired[], meta)  fired = [{ command, type, confidence }];
//                               meta = { errors, errorDetails } — hydrate/eval
//                               failures on this row (the row still reports so
//                               progress never stalls). errorDetails =
//                               [{ stage: "hydrate"|"eval"|"fetch", command,
//                               message }] so surfaces can GROUP failures by
//                               cause — a bare count ("116 errors") isn't
//                               actionable; "116× eval · NameError: ..." is.
//                               "fetch" = a planned context fetch failed /
//                               was over budget, so the eval never ran.
//   onPlanStats(stats)          batch path only: { requested, fetches,
//                               merged, rounds } — how many per-event context
//                               queries the planner folded into how many
//                               fetches ("120 queries planned into 7 fetches").
//   shouldStop()                cooperative cancel (re-run / unmount)
//
// Routing: triggers that need per-event context I/O take a BATCHED
// path — hydrate everything first, then:
//   - declared form (analytics_query steps): collect every
//     (trigger × event) substituted spec, run them through the query
//     planner (dedupe + IN-merge), evaluate with rows pre-bound via
//     runKernel's `precomputed`;
// Cheap all-python triggers keep the original
// hydrate-and-evaluate streaming path untouched.
const errMessage = (e) => String((e && (e.message || e)) || "unknown error").slice(0, 300);

// Concurrency cap for planned context fetches, and the per-scan fetch
// budget: a scan that still needs more than FETCH_BUDGET fetches after
// planning fails those evaluations loudly (stage:"fetch") instead of
// silently hammering the analytics API.
const FETCH_CONCURRENCY = 4;
const FETCH_BUDGET = 50;

// In-browser scan only: dispatch to the batched lookup or streaming
// (cheap) path. The hybrid entry point below routes here for the triggers the
// backend ISN'T serving.
async function runClientScan(traceIds, triggers, { concurrency = 6, onResult, shouldStop, onPlanStats } = {}) {
    const list = Array.isArray(triggers) ? triggers : [];
    if (list.some((t) => triggerNeedsBatch(t))) {
        return runBatchedScan(traceIds, list, { concurrency, onResult, shouldStop, onPlanStats });
    }
    return runStreamingScan(traceIds, list, { concurrency, onResult, shouldStop });
}

function triggerNeedsBatch(trigger) {
    return hasAnalyticsSteps(trigger);
}

function hasAnalyticsSteps(trigger) {
    return Array.isArray(trigger?.steps) && trigger.steps.some((s) => s?.kind === "analytics_query");
}

// ── 5b. Backend-served signals (read durable signal_tags) ──────────
//
// A trigger whose emitted namespace the backend is actively producing
// (`trigger.family` ∈ the active set) is READ from signal_tags rather than
// re-run in the browser. As the backend ports + enables more signals they
// appear in the probe and migrate automatically — no dashboard change. Triggers
// the backend isn't producing keep the in-browser scan untouched.

let _activeNsPromise = null;
onAuthIdentityChange(() => { _activeNsPromise = null; });
export function _resetBackendSignalsCache() { _activeNsPromise = null; } // test-only

function getBackendServedNamespaces() {
    if (!_activeNsPromise) {
        const epoch = getAuthIdentityEpoch();
        const request = getActiveSignalNamespaces().then((namespaces) => {
            if (getAuthIdentityEpoch() !== epoch) return new Set();
            return namespaces;
        });
        _activeNsPromise = request;
    }
    return _activeNsPromise;
}

// trigger → the namespace it emits. The extraction signals declare it as
// `family` (skill / memory / mcp_server). Security signals embed the namespace in
// their python source and aren't routable yet, so a trigger with no `family`
// never matches and stays client-run.
function triggerNamespace(trigger) {
    return typeof trigger?.family === "string" && trigger.family ? trigger.family : null;
}

// BE verdicts for the served triggers over a window → Map<event_id, fired[]>.
// Each fired entry mirrors the client-run shape ({command, type, value,
// confidence?}); one per (event, served trigger that has tags). Namespaces match
// triggers EXACTLY, so a `skill.fingerprint` facet row never lands on `skill`.
async function fetchBackendVerdicts(traceIds, beTriggers, range) {
    const rows = await getSignalTags(traceIds, range);
    const byEvent = new Map(); // event_id -> (namespace -> tag[])
    for (const r of rows) {
        if (!r || !r.event_id || !r.namespace) continue;
        let nsMap = byEvent.get(r.event_id);
        if (!nsMap) { nsMap = new Map(); byEvent.set(r.event_id, nsMap); }
        let arr = nsMap.get(r.namespace);
        if (!arr) { arr = []; nsMap.set(r.namespace, arr); }
        arr.push({ subject_type: r.subject_type, namespace: r.namespace, tag: r.tag, value: r.value });
    }
    const out = new Map();
    for (const [eventId, nsMap] of byEvent) {
        const fired = [];
        for (const trig of beTriggers) {
            const tags = nsMap.get(triggerNamespace(trig));
            if (!tags || !tags.length) continue;
            const ann = tagsToAnnotation(tags);
            if (!ann) continue;
            fired.push({
                command: trig.command,
                type: ann.type,
                value: ann.value,
                ...(typeof ann.confidence === "number" ? { confidence: ann.confidence } : {}),
            });
        }
        if (fired.length) out.set(eventId, fired);
    }
    return out;
}

// All requested triggers are BE-served (e.g. the per-signal detail lens on a
// backend signal): no kernel scan — stream the fetched verdicts per trace so the
// store fills and progress settles, same callback contract as the scan paths
// (one onResult per id, firing or not).
function emitBackendOnly(traceIds, beByTrace, { onResult, shouldStop } = {}) {
    for (const id of (Array.isArray(traceIds) ? traceIds : [])) {
        if (shouldStop?.()) break;
        try { onResult?.(id, beByTrace.get(id) || [], { errors: 0, errorDetails: [] }); } catch { /* noop */ }
    }
}

// Client-side signal scanning is OFF by default: the dashboard READS verdicts
// from the backend signal_tags rather than re-running kernels in the browser, so
// what it shows always matches what the backend persists. The in-browser path
// stays here, gated, so it can be re-enabled without a code revert (e.g. to
// preview a signal the backend isn't producing yet).
let _clientScanEnabled = false;
export function _setClientSignalScan(on) { _clientScanEnabled = !!on; } // flag / test hook

// Run signals over an existing window of trace ids. Triggers the backend is
// actively producing are READ from signal_tags. By default that is the ONLY
// source — a trigger the backend isn't producing simply doesn't fire (no client
// compute). With the client scan re-enabled, the unserved triggers also run
// in-browser and merge per trace into one fired[] (same store, same shape).
// `opts.range` scopes the signal_tags fetch (defaults to the analytics 30d); a
// query failure degrades to whatever the client scan produces (empty if off).
export async function runSignalsOverTraceIds(traceIds, triggers, opts = {}) {
    const list = Array.isArray(triggers) ? triggers : [];
    const range = opts.range ?? null;
    let active;
    try { active = await getBackendServedNamespaces(); } catch { active = new Set(); }
    const isServed = (t) => { const ns = triggerNamespace(t); return ns != null && active.has(ns); };
    const beTriggers = list.filter(isServed);

    const beByTrace = beTriggers.length
        ? await fetchBackendVerdicts(traceIds, beTriggers, range).catch(() => new Map())
        : new Map();

    const clientTriggers = _clientScanEnabled ? list.filter((t) => !isServed(t)) : [];
    if (!clientTriggers.length) {
        emitBackendOnly(traceIds, beByTrace, opts);
        return;
    }
    const mergeOnResult = (id, fired, meta) => {
        const be = beByTrace.get(id);
        opts.onResult?.(id, be && be.length ? [...fired, ...be] : fired, meta);
    };
    return runClientScan(traceIds, clientTriggers, { ...opts, onResult: mergeOnResult });
}

// ── 5a. Streaming path (cheap triggers) — unchanged behavior ────────
async function runStreamingScan(traceIds, triggers, { concurrency, onResult, shouldStop }) {
    let next = 0;
    async function worker() {
        while (next < traceIds.length) {
            if (shouldStop?.()) return;
            const id = traceIds[next++];
            const fired = [];
            const errorDetails = [];
            try {
                const event = await hydratePrompt(id);
                if (event) {
                    for (const trig of triggers) {
                        if (shouldStop?.()) return;
                        try {
                            const r = await runTrigger(trig, event);
                            if (r.error) errorDetails.push({ stage: "eval", command: trig.command, message: errMessage(r.error) });
                            else if (r.fired && r.annotation) {
                                fired.push({ command: trig.command, type: r.annotation.type, confidence: r.annotation.confidence, value: r.annotation.value ?? null });
                            }
                        } catch (err) {
                            // a single trigger erroring shouldn't drop the row
                            errorDetails.push({ stage: "eval", command: trig.command, message: errMessage(err) });
                        }
                    }
                } else {
                    errorDetails.push({ stage: "hydrate", command: null, message: "hydrate returned no row" });
                }
            } catch (err) {
                // hydrate failed — row simply gets no badge
                errorDetails.push({ stage: "hydrate", command: null, message: errMessage(err) });
            }
            if (!shouldStop?.()) onResult?.(id, fired, { errors: errorDetails.length, errorDetails });
        }
    }
    const n = Math.min(Math.max(1, concurrency), Math.max(1, traceIds.length));
    await Promise.all(Array.from({ length: n }, () => worker()));
}

// ── 5b. Batched path (lookup triggers): hydrate → plan → evaluate ───
//
// Request keys are `${eventId}|${command}|${stepId}` — opaque to the
// planner, parsed back here only via the maps below.
async function runBatchedScan(traceIds, triggers, { concurrency, onResult, shouldStop, onPlanStats }) {
    // Phase 1 — hydrate everything (same per-row concurrency as the
    // streaming path). A row whose hydrate fails reports IMMEDIATELY,
    // exactly like the streaming path would, and drops out of the scan.
    const events = new Map(); // id -> hydrated event
    {
        let next = 0;
        async function hydrateWorker() {
            while (next < traceIds.length) {
                if (shouldStop?.()) return;
                const id = traceIds[next++];
                try {
                    const event = await hydratePrompt(id);
                    if (event) events.set(id, event);
                    else if (!shouldStop?.()) {
                        onResult?.(id, [], { errors: 1, errorDetails: [{ stage: "hydrate", command: null, message: "hydrate returned no row" }] });
                    }
                } catch (err) {
                    if (!shouldStop?.()) {
                        onResult?.(id, [], { errors: 1, errorDetails: [{ stage: "hydrate", command: null, message: errMessage(err) }] });
                    }
                }
            }
        }
        const n = Math.min(Math.max(1, concurrency), Math.max(1, traceIds.length));
        await Promise.all(Array.from({ length: n }, () => hydrateWorker()));
    }
    if (shouldStop?.()) return;

    const stats = { requested: 0, fetches: 0, merged: 0, rounds: 0 };
    const emitStats = () => {
        if (stats.requested > 0 && !shouldStop?.()) onPlanStats?.({ ...stats });
    };

    // Phase 2 — declared prefetch: substitute each analytics step's spec
    // per event, plan the whole batch, execute the fetches, slice rows
    // back per request key. Failures land per-key so phase 3 can fail
    // just the affected (trigger × event) evals with stage:"fetch".
    const plannedTriggers = triggers.filter((t) => hasAnalyticsSteps(t));
    const prefetchedRows = new Map();   // requestKey -> rows
    const prefetchErrors = new Map();   // requestKey -> message
    if (plannedTriggers.length && events.size) {
        const requests = [];
        for (const trig of plannedTriggers) {
            for (const [id, event] of events) {
                for (const step of trig.steps) {
                    if (step?.kind !== "analytics_query") continue;
                    const key = `${id}|${trig.command}|${step.id}`;
                    const spec = substituteParams(step.spec, { event });
                    const unbound = fieldsWithoutValue(spec);
                    if (unbound.length) {
                        prefetchErrors.set(key, missingValueMessage(unbound));
                        continue;
                    }
                    requests.push({ key, spec });
                }
            }
        }
        const plan = planQueries(requests);
        stats.requested += plan.stats.requested;
        stats.merged += plan.stats.merged;
        const runnable = plan.fetches.slice(0, FETCH_BUDGET);
        stats.fetches += runnable.length;
        for (const fetch of plan.fetches.slice(FETCH_BUDGET)) {
            for (const a of fetch.assign) {
                prefetchErrors.set(a.key, `context-fetch budget exceeded — scan planned ${plan.fetches.length} fetches, budget is ${FETCH_BUDGET}`);
            }
        }
        await mapConcurrent(runnable, FETCH_CONCURRENCY, async (fetch) => {
            if (shouldStop?.()) return;
            try {
                const rows = await runQueryRows(fetch.spec);
                for (const [key, sliced] of sliceResults(fetch, rows)) prefetchedRows.set(key, sliced);
            } catch (err) {
                for (const a of fetch.assign) prefetchErrors.set(a.key, errMessage(err));
            }
        });
        emitStats();
    }
    if (shouldStop?.()) return;

    // Phase 3 — evaluate every (trigger × event), one onResult per row
    // as ALL of that row's triggers settle.
    const limit = createLimiter(Math.max(1, concurrency));
    const perRow = [...events.entries()].map(([id, event]) => {
        const verdicts = triggers.map((trig) =>
            limit(() => evalForRow(trig, id, event)));
        return Promise.all(verdicts).then((settled) => {
            if (shouldStop?.()) return;
            const fired = settled.filter((v) => v.fired).map((v) => v.fired);
            const errorDetails = settled.filter((v) => v.errorDetail).map((v) => v.errorDetail);
            onResult?.(id, fired, { errors: errorDetails.length, errorDetails });
        });
    });

    // One trigger's verdict for one row → { fired? } | { errorDetail? } | {}.
    async function evalForRow(trig, id, event) {
        if (shouldStop?.()) return {};
        try {
            let verdict;
            if (hasAnalyticsSteps(trig)) {
                const precomputed = {};
                for (const step of trig.steps) {
                    if (step?.kind !== "analytics_query") continue;
                    const key = `${id}|${trig.command}|${step.id}`;
                    if (prefetchErrors.has(key)) {
                        return { errorDetail: { stage: "fetch", command: trig.command, message: prefetchErrors.get(key) } };
                    }
                    if (!prefetchedRows.has(key)) {
                        // Fetch never ran (scan cancelled mid-flight) — say so
                        // rather than evaluating against silently-empty rows.
                        return { errorDetail: { stage: "fetch", command: trig.command, message: "context fetch was not executed" } };
                    }
                    precomputed[step.id] = prefetchedRows.get(key);
                }
                verdict = await runTrigger(trig, event, { precomputed });
            } else {
                verdict = await runTrigger(trig, event);
            }
            return verdictRecord(trig, verdict);
        } catch (err) {
            return { errorDetail: { stage: "eval", command: trig.command, message: errMessage(err) } };
        }
    }

    await Promise.all(perRow);
    emitStats();
}

// Trigger verdict → the per-row record phase 3 aggregates.
function verdictRecord(trig, verdict) {
    if (verdict.error) return { errorDetail: { stage: "eval", command: trig.command, message: errMessage(verdict.error) } };
    if (verdict.fired && verdict.annotation) {
        return { fired: { command: trig.command, type: verdict.annotation.type, confidence: verdict.annotation.confidence, value: verdict.annotation.value ?? null } };
    }
    return {};
}

// Minimal promise-concurrency limiter (no deps): at most `n` factory
// invocations in flight; the returned promise settles with the task's.
function createLimiter(n) {
    let active = 0;
    const queue = [];
    const pump = () => {
        while (active < n && queue.length) {
            const { task, resolve, reject } = queue.shift();
            active += 1;
            Promise.resolve()
                .then(task)
                .then(resolve, reject)
                .finally(() => { active -= 1; pump(); });
        }
    };
    return (task) => new Promise((resolve, reject) => {
        queue.push({ task, resolve, reject });
        pump();
    });
}

// Run `fn` over `items` with at most `n` in flight. Rejections are the
// caller's to handle inside `fn` (the planner paths catch per-fetch).
async function mapConcurrent(items, n, fn) {
    let next = 0;
    async function worker() {
        while (next < items.length) {
            const i = next++;
            await fn(items[i], i);
        }
    }
    const count = Math.min(Math.max(1, n), Math.max(1, items.length));
    await Promise.all(Array.from({ length: count }, () => worker()));
}

// A trigger is "cheap" — safe to run over every visible row live — when
// every step is pure python (no analytics_query / agent / mcp_tool I/O)
// and none of that Python attempts the forbidden host bridge.
export function isCheapTrigger(trigger) {
    const steps = trigger?.steps;
    return Array.isArray(steps) && steps.length > 0
        && steps.every((s) => s.kind === "python")
        && !usesCtxQuery(trigger);
}

// Does any Python step reach for the removed async context bridge?
// Textual containment is deliberate: the harness refuses legacy
// definitions before execution and directs authors to declared query steps.
export function usesCtxQuery(trigger) {
    const steps = trigger?.steps;
    return Array.isArray(steps) && steps.some((s) =>
        s?.kind === "python" && typeof s.code === "string" && s.code.includes("ctx.query"));
}

// ── helpers ────────────────────────────────────────────────────────

// Structured turns a signal scopes over: { role, text, blocks }. `text` is the
// turn's flattened text (the surface the 15 text-reading signals scan); `blocks`
// is the normalized, provider-agnostic block list parseApiRequest already
// produced (text / tool_use {id,name,input} / tool_result {tool_use_id,content,
// isError,linkedToolName} / thinking / image / unknown). Carrying `blocks` makes
// `event.turns` the *normalized api_request*: a surface-extraction signal (skill,
// memory) pairs tool_use↔tool_result over canonical blocks and never matches a
// per-provider shape. Additive — the text-reading signals read only role/text.
function normalizedTurns(turns) {
    if (!Array.isArray(turns)) return [];
    return turns.map((t) => ({
        role: t?.role || "",
        text: turnText(t),
        blocks: Array.isArray(t?.blocks) ? t.blocks : [],
    }));
}

// Blocks of `type` from the NEWEST turn that has any (walks from the end).
// "Current turn only" — agent prompts re-send the whole transcript every
// turn, so scanning all turns would re-fire a signal on every later event;
// scoping to the latest tool_use / tool_result turn fires it once.
function lastTurnBlocks(turns, type) {
    if (!Array.isArray(turns)) return [];
    for (let i = turns.length - 1; i >= 0; i--) {
        const blocks = Array.isArray(turns[i]?.blocks) ? turns[i].blocks : [];
        const hit = blocks.filter((b) => b && b.type === type);
        if (hit.length) return hit;
    }
    return [];
}

// Current turn's tool calls as [{ name, arguments }] — matching the backend
// `extract_tool_calls`. `arguments` is the raw dict-or-string the provider
// sent (NOT pre-stringified); a signal stringifies it itself, exactly as a
// backend signal would. (Claude double-encodes some object params — a signal
// regexing args should `json.dumps` a dict before matching.)
function lastToolCalls(turns) {
    return lastTurnBlocks(turns, "tool_use").map((b) => ({
        name: typeof b.name === "string" ? b.name : "",
        arguments: b.input != null ? b.input : null,
    }));
}

// Current turn's tool results as
// [{ name, content, content_length_bytes, is_error, content_truncated }]
// — `name`/`content`/`content_length_bytes` match the backend `_make_tool_result`;
// `is_error` is a dashboard-optional extension. `content_truncated` is always
// false here: the harness reads the conversation record, which is never trimmed;
// only the trace-analysis Turn is.
function lastToolResults(turns) {
    return lastTurnBlocks(turns, "tool_result").map((b) => {
        const content = toContentString(b.content);
        return {
            name: typeof b.linkedToolName === "string" ? b.linkedToolName : "",
            content,
            content_length_bytes: utf8Len(content),
            is_error: !!b.isError,
            content_truncated: false,
        };
    });
}

function utf8Len(s) {
    try { return new TextEncoder().encode(s).length; } catch { return (s || "").length; }
}

// Distinct invoked tool names — mirrors the backend's `tools_used` derivation.
function deriveToolsUsed(toolCalls) {
    const seen = [];
    for (const c of toolCalls || []) {
        const n = c && typeof c.name === "string" ? c.name : "";
        if (n && !seen.includes(n)) seen.push(n);
    }
    return seen;
}

// Files / URLs touched in this turn's tool-call arguments. The backend's
// `extract_resource_paths` (parse/extractors.py) is authoritative — this is the
// dashboard's best-effort mirror: URLs, and whitespace tokens that look like a
// path (leading ~// or containing /) with no shell metacharacters.
function deriveResourcePaths(toolCalls) {
    const out = [];
    const seen = new Set();
    const push = (v) => { if (typeof v === "string" && v && !seen.has(v)) { seen.add(v); out.push(v); } };
    for (const c of toolCalls || []) {
        const a = c && c.arguments;
        const vals = typeof a === "string" ? [a]
            : (a && typeof a === "object" ? Object.values(a).filter((v) => typeof v === "string") : []);
        for (const v of vals) {
            for (const url of v.match(/https?:\/\/[^\s)<>"']+/g) || []) push(url);
            for (const tok of v.split(/\s+/)) {
                if (/[<>|&;$(){}!=`]/.test(tok)) continue;
                if (/^~?\//.test(tok) || (tok.includes("/") && !tok.includes("://"))) push(tok);
            }
        }
        if (out.length >= 64) break;
    }
    return out.slice(0, 64);
}

// A tool definition the model was offered, with its JSON Schema flattened
// to a string so a signal can scan name + description + schema together.
function simpleToolDef(d) {
    let schemaText = "";
    try { schemaText = d?.typedSchema ? formatConversationValue(d.typedSchema) : d?.schema != null ? JSON.stringify(d.schema) : ""; } catch { schemaText = ""; }
    return {
        name: typeof d?.name === "string" ? d.name : "",
        description: typeof d?.description === "string" ? d.description : "",
        schema_text: schemaText,
    };
}



// Exposed for unit tests. The tool-surface extraction (`tool_calls` /
// `tool_results` / `tool_defs` shapes) IS the dashboard's signal contract, so
// it's covered directly rather than only through hydratePrompt's fetch/parse
// path. Not part of the public harness API — do not import outside tests.
export const __testables = {
    lastTurnBlocks,
    lastToolCalls,
    lastToolResults,
    toContentString,
    simpleToolDef,
    deriveToolsUsed,
    deriveResourcePaths,
    tagsToAnnotation,
};
